/**
 * useNeedsAttention — React Query hook for the "Needs attention" tray
 *
 * Wraps the `needs-attention.service` HTTP calls behind React Query so:
 *   - the list re-fetches on filter change
 *   - mutations invalidate the list automatically
 *   - components don't have to manage loading / error state by hand
 *
 * Issue #322 B.2 (frontend).
 */

import type {
  AttentionRow,
  ListNeedsAttentionResponse,
  ResolveAction,
  ResolveOneResponse,
  BulkResolveResult,
  ReprocessUnknownRowsRequest,
  ReprocessUnknownRowsResponse,
} from '@bat/shared'
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query'

import { defaultQueryRetry, transactionKeys } from '@/platform/query-client'
import { extractDisplayableErrorMessage } from '@/shared/utils/displayable-error-message'

import { assetAliasKeys } from './useAssetAliases'
import {
  listNeedsAttention,
  resolveOne,
  bulkResolve,
  reprocessUnknownRows,
  type NeedsAttentionFilters,
} from '../services/needs-attention.service'

// ---------------------------------------------------------------------------
// Query key factory — keyed by the exact filters so distinct filter combos
// don't share a cache slot. Mirror the `importSourceKeys` pattern.
// ---------------------------------------------------------------------------

export const needsAttentionKeys = {
  all: ['needsAttention'] as const,
  list: (filters: NeedsAttentionFilters) => ['needsAttention', 'list', filters] as const,
} as const

// ---------------------------------------------------------------------------
// List query
// ---------------------------------------------------------------------------

export interface UseNeedsAttentionListResult {
  rows: AttentionRow[]
  total: number
  page: number
  limit: number
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: string | null
  refetch: () => void

  /**
   * ACCOUNT-WIDE, never page- or filter-scoped: the number of unrecognised rows
   * that have a template. This is the set `POST .../reprocess` sweeps when the
   * tray button sends `{}`, so it — and not the rows on screen — is what
   * decides whether the button exists.
   */
  unrecognisedRowsWithTemplateCount: number
  /** Of those, the ones whose template classifies row by row. */
  standaloneReprocessableRowCount: number
  /** De las bloqueadas, las que lo están porque la plantilla AGRUPA filas. */
  groupingBlockedRowCount: number
  /** De las bloqueadas, las que lo están por ENRUTADO DE VARIANTE. */
  variantRoutingBlockedRowCount: number
}

/**
 * Fetch the current tenant's needs-attention rows. Default page=1, limit=50
 * matches the backend defaults so callers can omit them.
 */
export function useNeedsAttentionList(
  filters: NeedsAttentionFilters = {},
): UseNeedsAttentionListResult {
  const query = useQuery({
    queryKey: needsAttentionKeys.list(filters),
    queryFn: (): Promise<ListNeedsAttentionResponse> => listNeedsAttention(filters),
    // Tray data is "user-driven" (changes only after an import or a manual
    // resolve), so a longer stale time keeps the tab snappy across tab
    // switches. Resolve mutations invalidate explicitly below.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: defaultQueryRetry,
    // Pagination UX: keep the previous page's data (and thus `total`)
    // on screen while the next page loads. Without this, `total` falls
    // back to 0 mid-fetch, the tray's page clamp sees "1 page" and
    // bounces the user back to page 1.
    placeholderData: keepPreviousData,
  })

  return {
    rows: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? filters.page ?? 1,
    limit: query.data?.limit ?? filters.limit ?? 50,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    // `apiService` throws an ApiException whose `message` is the EMPTY STRING for a
    // transport failure, which left the tray printing "Error al cargar: motivo
    // desconocido" over a perfectly identifiable NETWORK_ERROR. Fall back to the code
    // before the generic phrase — same order the admin surfaces settled on.
    error: extractDisplayableErrorMessage(query.error),
    refetch: query.refetch,
    // 0 while loading/erroring, which hides the button rather than offering an
    // account-wide write over a count we do not have.
    unrecognisedRowsWithTemplateCount: query.data?.unrecognisedRowsWithTemplateCount ?? 0,
    standaloneReprocessableRowCount: query.data?.standaloneReprocessableRowCount ?? 0,
    // La CAUSA del bloqueo, en dos cifras: sin ellas la frase del control
    // apagado sólo puede afirmar una de las dos, y afirmaba la que era falsa
    // para 24 de 26 filas (medido el 2026-09-01).
    groupingBlockedRowCount: query.data?.groupingBlockedRowCount ?? 0,
    variantRoutingBlockedRowCount: query.data?.variantRoutingBlockedRowCount ?? 0,
  }
}

// ---------------------------------------------------------------------------
// Single-row resolve mutation
// ---------------------------------------------------------------------------

interface ResolveOneArgs {
  /**
   * Staging row ID (`imported_transactions.id`) — this is the id the
   * `:txId` resolve route matches against. NOT the ledger
   * `transactions.id`. In an `AttentionRow` this is `row.id`, not
   * `row.transactionId`.
   */
  stagingId: string
  resolution: ResolveAction
}

/**
 * Mutation hook for `POST /api/imports/needs-attention/:txId/resolve`.
 * Invalidates the entire `needsAttention` cache on success so the list
 * picks up the row dropping out (or count decrementing).
 */
export function useResolveNeedsAttention() {
  const queryClient = useQueryClient()

  return useMutation<ResolveOneResponse, Error, ResolveOneArgs>({
    mutationFn: ({ stagingId, resolution }): Promise<ResolveOneResponse> =>
      resolveOne(stagingId, resolution),
    // Non-idempotent write: the first call detaches/discards the staging row,
    // so the global `mutations.retry: 1` would re-resolve an already-resolved
    // row and surface a spurious 409 ("not in 'warning' state" / "no linked
    // ledger transaction") as a red error. Never retry resolve.
    retry: false,
    onSuccess: () => invalidateAfterResolve(queryClient),
  })
}

// ---------------------------------------------------------------------------
// Bulk resolve mutation
// ---------------------------------------------------------------------------

interface BulkResolveArgs {
  /** Staging-row IDs (= `imported_transactions.id`) per backend contract */
  txIds: string[]
  resolution: ResolveAction
}

/**
 * Mutation hook for `POST /api/imports/needs-attention/bulk-resolve`.
 */
export function useBulkResolveNeedsAttention() {
  const queryClient = useQueryClient()

  return useMutation<BulkResolveResult, Error, BulkResolveArgs>({
    mutationFn: ({ txIds, resolution }): Promise<BulkResolveResult> =>
      bulkResolve(txIds, resolution),
    // Non-idempotent write (see useResolveNeedsAttention) — don't retry.
    retry: false,
    onSuccess: () => invalidateAfterResolve(queryClient),
  })
}

/**
 * ⚠️⚠️ A resolution writes THREE things, not one. `map_asset` saves the ticker
 * as a tenant alias AND creates the ledger transaction; only the tray used to be
 * refetched. Measured in the UI (28/09/2026): resolving ZZTOPX → SOL returned
 * `resolved: 1`, the row left the tray, and the «Alias de tickers guardados»
 * panel right below kept saying «Todavía no has guardado ningún alias» until a
 * page reload — although the dialog had just promised the alias would appear
 * there. Same set `useReprocessUnknownRows` already refreshes, plus the aliases.
 */
function invalidateAfterResolve(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: needsAttentionKeys.all })
  queryClient.invalidateQueries({ queryKey: assetAliasKeys.all })
  queryClient.invalidateQueries({ queryKey: transactionKeys.all })
}

// ---------------------------------------------------------------------------
// Reprocess unknown-operation rows mutation
// ---------------------------------------------------------------------------

/**
 * Mutation hook for `POST /api/imports/unknown-rows/reprocess`.
 *
 * Re-runs the CURRENT template rules over stored `unknown_operation` rows.
 * Rows that now classify are promoted to the ledger; the rest stay unknown.
 *
 * Non-idempotent (writes ledger rows) — `retry: false` so a failed write is
 * never re-issued. On success invalidate BOTH:
 *   - the needs-attention cache (promoted rows drop out of the tray), AND
 *   - the transactions cache (newly-promoted ledger rows must appear on the
 *     /transactions page without a manual refresh).
 */
export function useReprocessUnknownRows() {
  const queryClient = useQueryClient()

  return useMutation<ReprocessUnknownRowsResponse, Error, ReprocessUnknownRowsRequest>({
    mutationFn: (request): Promise<ReprocessUnknownRowsResponse> => reprocessUnknownRows(request),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: needsAttentionKeys.all })
      queryClient.invalidateQueries({ queryKey: transactionKeys.all })
    },
  })
}
